// src/components/runs/RunsTable.jsx
import React, { useState } from 'react';
import { Loader2, MoreHorizontal } from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const RunsTable = ({
  runs,
  loading,
  error,
  pagination,
  onPageChange,
  onEdit,
  onCancel,
  onView,
  onStatusChange,
}) => {
  const { theme } = useTheme();
  const [editingStatusId, setEditingStatusId] = useState(null);

  const handlePrev = () => {
    if (pagination.page > 1) {
      onPageChange(pagination.page - 1);
    }
  };

  const handleNext = () => {
    if (pagination.page < pagination.totalPages) {
      onPageChange(pagination.page + 1);
    }
  };

  const formatRunDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, '0')}/${String(
      d.getMonth() + 1
    ).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
  };

  const primary600 = theme?.primary?.[600] || '#0f172a';
  const secondary600 = theme?.secondary?.[600] || '#4f46e5';

  const modeBadgeClasses = (mode) =>
    mode === 'auto'
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
      : 'bg-amber-50 text-amber-700 border border-amber-100';

  const statusBadgeClasses = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-100';
      case 'in_progress':
        return 'bg-sky-50 text-sky-700 border border-sky-100';
      case 'scheduled':
        return 'bg-indigo-50 text-indigo-700 border border-indigo-100';
      case 'cancelled':
        return 'bg-rose-50 text-rose-700 border border-rose-100';
      default:
        return 'bg-slate-50 text-slate-700 border border-slate-100';
    }
  };

  const handleStatusChange = (run, newStatus) => {
    if (newStatus === run.status) {
      setEditingStatusId(null);
      return;
    }
    if (onStatusChange) {
      onStatusChange(run, newStatus);
    }
    setEditingStatusId(null);
  };

  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm border border-slate-200">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-800">Scheduled Runs</h2>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10 text-slate-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading runs...
        </div>
      )}

      {!loading && error && (
        <div className="py-6 text-center text-sm text-rose-500">
          {error}
        </div>
      )}

      {!loading && !error && runs.length === 0 && (
        <div className="py-8 text-center text-sm text-slate-500">
          No runs found. Create your first schedule run.
        </div>
      )}

      {!loading && !error && runs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm text-slate-700">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="py-2 pr-4 font-semibold">S.No</th> {/* NEW */}
                <th className="py-2 pr-4 font-semibold">Date</th>
                <th className="py-2 pr-4 font-semibold">Guard</th>
                <th className="py-2 pr-4 font-semibold">Title</th>
                <th className="py-2 pr-4 font-semibold">Mode</th>
                <th className="py-2 pr-4 font-semibold">Status</th>
                <th className="py-2 pr-4 text-right font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {runs.map((run, index) => {
                const serial =
                  (pagination.page - 1) * pagination.limit + index + 1;

                return (
                  <tr
                    key={run.run_id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                  >
                    <td className="py-2 pr-4 align-middle text-xs text-slate-700">
                      {serial}
                    </td>

                    <td className="py-2 pr-4 align-middle text-xs text-slate-700">
                      {formatRunDate(run.run_date)}
                    </td>

                    <td className="py-2 pr-4 align-middle text-xs">
                      <button
                        type="button"
                        onClick={() => onView && onView(run)}
                        className="text-sky-600 hover:text-sky-700 hover:underline font-medium"
                      >
                        {run.guard_first_name
                          ? `${run.guard_first_name} ${run.guard_last_name || ''} ${
                              run.guard_code ? `(${run.guard_code})` : ''
                            }`
                          : `Guard #${run.guard_id}`}
                      </button>
                    </td>

                    <td className="py-2 pr-4 align-middle text-xs text-slate-700">
                      {run.title || '-'}
                    </td>

                    <td className="py-2 pr-4 align-middle text-xs">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${modeBadgeClasses(
                          run.upload_mode
                        )}`}
                      >
                        {run.upload_mode}
                      </span>
                    </td>

                    {/* STATUS */}
                    <td className="py-2 pr-4 align-middle text-xs">
                      {editingStatusId === run.run_id ? (
                        <div
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${statusBadgeClasses(
                            run.status
                          )}`}
                        >
                          <select
                            value={run.status || ''}
                            onChange={(e) =>
                              handleStatusChange(run, e.target.value)
                            }
                            onBlur={() => setEditingStatusId(null)}
                            className="bg-transparent outline-none cursor-pointer capitalize text-[11px]"
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingStatusId(run.run_id)}
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${statusBadgeClasses(
                            run.status
                          )}`}
                        >
                          {String(run.status || '').replace('_', ' ')}
                        </button>
                      )}
                    </td>

                    <td className="py-2 pr-4 text-right align-middle text-xs">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit(run)}
                          className="rounded-full px-3 py-1 text-[11px] font-medium text-white shadow-sm hover:shadow-md"
                          style={{
                            background: `linear-gradient(135deg, ${primary600} 0%, ${secondary600} 100%)`,
                          }}
                        >
                          Edit
                        </button>

                        {run.status !== 'cancelled' && (
                          <button
                            type="button"
                            onClick={() => onCancel(run)}
                            className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-100"
                          >
                            Cancel
                          </button>
                        )}

                        <button
                          type="button"
                          className="rounded-full border border-slate-200 bg-white p-1 text-slate-500 hover:bg-slate-50"
                        >
                          <MoreHorizontal className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-xs text-slate-600">
        <span>
          Page {pagination.page} of {pagination.totalPages}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handlePrev}
            disabled={pagination.page <= 1}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1 disabled:opacity-40 hover:bg-slate-50"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={pagination.page >= pagination.totalPages}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1 disabled:opacity-40 hover:bg-slate-50"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
};

export default RunsTable;
